import { IGuideline } from '../../app/content/workspace/workspace.models';
import { EventEmitter } from '../../lib/event-emitter';
import { getBoundingRectangleOfRotatedBox, getOuterBounds, isSameBounds } from '../../lib/geom';
import { cloneDeep, cloneMapDeep, getCookie, lerp, omit, roundToNearestMultiple, stringLiteralArray, uuidv4 } from '../../lib/utils';
import { ICollection } from '../domain/array';
import { IColor } from '../domain/color';
import { IBoundingBox, IBounds, IConstraint, IPosition, ISize } from '../domain/dimension';
import { IElement, IElementProperty } from '../domain/element';
import { IFeed } from '../domain/feed';
import { IFontFamilies, IFontStyle } from '../domain/font';
import { IKeyframe } from '../domain/keyframe';
import { IBorder, IPadding, IShadow, ITextShadow } from '../domain/style';
import * as T from '../domain/text';
import { IInlinedStyledTextSpan, IInlineStyledText, ITextSpan, IVersionedText, IVersionProperty } from '../domain/version';
import { IWidgetAsset, IWidgetSelectOption, WidgetUnits } from '../domain/widget';
import { Color } from './color';
import { RichText } from './rich-text';
import { ISerializedTextValue } from './serializer.value';
import { characterProperties, serializeVersionedText } from './text-nodes';
import { timingFunctions } from './timing-functions';
import { getTransitionKeyframesOnElement } from './transition';

export enum NodeKind {
    Creative = 'creative',
    Rectangle = 'rectangle',
    Text = 'text',
    Image = 'image',
    Group = 'group',
    Selection = 'selection',
    AnyElement = 'any',
    Ellipse = 'ellipse',
    Button = 'button',
    Widget = 'widget',
    BannerflowLibraryWidget = 'bannerflowLibraryWidget', // Only when bfl-widget is in bl
    BannerflowLibraryWidgetInstance = 'bannerflowLibraryWidgetInstance' // Only when bfl-widget is on canvas
}

export type PropertyUnit =
    'number' |
    'Color' |
    'text' |
    'Shadow[]' |
    'Border' |
    'FontStyle' |
    'ImageAsset' |
    'WidgetAsset' |
    'string' |
    'Padding' |
    'enum' |
    'CustomProperty[]' |
    'string[]' |
    'boolean';


export interface IImageAsset {
    id: string;
    url: string;
    __loading?: boolean;
    width?: number;
    height?: number;
}

export interface ICustomProperty extends IElementProperty { }

export type OneOfElements =
    IEllipseElement |
    IRectangleElement |
    IButtonElement |
    ITextElement |
    IImageElement |
    IGroupElement |
    IWidgetElement |
    IAnyElement;

export type OneOfSelectableElements =
    OneOfElements |
    ElementSelection;

export type OneOfGroupNode =
    IGroupElement |
    CreativeDataNode |
    ICreativeViewNode;

export type OneOfGroupDataNode =
    IGroupElementDataNode |
    IRootNode;

export type OneOfAssetNodes =
    IImageElement;

export type OneOfTextNodes =
    IButtonElement |
    ITextElement;

export type OneOfTextDataNodes =
    IButtonElementDataNode |
    ITextElementDataNode;

export type PropertyValue =
    string |
    number |
    boolean |
    IColor |
    IShadow[] |
    ITextShadow[] |
    IVersionedText |
    IBorder |
    IFontStyle |
    IImageAsset |
    IPadding |
    IWidgetAsset |
    ICustomProperty[] |
    IFeed |
    IVersionedText;

interface IChildren {
    readonly elements: any[];
}

export interface IId {
    id: string;
    elementCid?: string;
    cidIndex?: number;
    parentId?: string;

}

export interface IElementNode extends IId, IPosition, ISize, ISVGBackgroundNode {
    readonly kind: NodeKind;
    name?: string;
    constraint: IConstraint;
    ratio?: number;
    originX?: number;
    originY?: number;
    rotationX?: number;
    rotationY?: number;
    rotationZ?: number;
    mirrorX?: boolean;
    mirrorY?: boolean;
    scaleX?: number;
    scaleY?: number;
    opacity?: number;
    time: number;
    duration: number;
    hidden?: boolean;
    locked?: boolean;
    fitOption?: string;
    fitPositionX?: number;
    fitPositionY?: number;
    quality?: number;
    inTransition?: ITransition;
    feed?: IFeed;
    outTransition?: ITransition;
    __boundingBox?: IBoundingBox;
    __perspectiveElement?: HTMLDivElement;
    __rootElement?: HTMLDivElement;
    __data?: IElementDataNode;
    __rootNode?: CreativeDataNode;
    __noEmit?: boolean;
    __onChange?: (obj: OneOfElements, property: string | number | symbol, value: any) => void;
}

export interface ISVGBackgroundNode extends ISize {
    readonly kind: NodeKind;
    fill?: IColor;
    border?: IBorder;
    shadows?: IShadow[];
    radius?: number;
    imageAsset?: IImageAsset;
    __rootElement?: HTMLDivElement;
    __backgroundElement?: SVGSVGElement;
}

export interface IElementDataNode extends IId {
    kind: NodeKind;
    name?: string;
    ratio?: number;
    locked: boolean;
    hidden: boolean;
    constraint: IConstraint;
    imageAsset?: IImageAsset;
    feed?: IFeed;
    time: number;
    duration: number;
    x: IKeyframe[];
    y: IKeyframe[];
    width: IKeyframe[];
    height: IKeyframe[];
    radius: IKeyframe[];
    mirrorX: IKeyframe[];
    mirrorY: IKeyframe[];
    scaleX: IKeyframe[];
    scaleY: IKeyframe[];
    opacity: IKeyframe[];
    originX: IKeyframe[];
    originY: IKeyframe[];
    rotationX: IKeyframe[];
    rotationY: IKeyframe[];
    rotationZ: IKeyframe[];

    // Animatable properties
    fill?: IKeyframe[];
    border?: IKeyframe[];
    shadows?: IKeyframe[];
    fitOption?: IKeyframe[];
    quality?: IKeyframe[];
    fitPositionX?: IKeyframe[];
    fitPositionY?: IKeyframe[];
    inTransition?: ITransition;
    outTransition?: ITransition;
    __noEmit?: boolean;
    __rootNode?: CreativeDataNode;
}
export interface IRectangleElementDataNode extends IElementDataNode {
    readonly kind: NodeKind.Rectangle;
}
export interface IEllipseElementDataNode extends IElementDataNode {
    readonly kind: NodeKind.Ellipse;
}
export interface ITextElementDataNode extends ITextDataNode {
    readonly kind: NodeKind.Text;
}
export interface IButtonElementDataNode extends ITextDataNode {
    readonly kind: NodeKind.Button;
}

export interface IImageElementDataNode extends IElementDataNode {
    readonly kind: NodeKind.Image;
    imageAsset?: IImageAsset;
    feed?: IFeed;
    parentId?: string;
}

interface IImage {
    id: string;
    src: string;
}

export type OneOfCustomPropertyValue =
    string |
    number |
    boolean |
    IWidgetSelectOption[] |
    IColor |
    IFontStyle |
    IImage |
    IFeed |
    T.IText;

export interface IWidgetCustomProperty {
    name: string;
    label: string;
    value: OneOfCustomPropertyValue | ISerializedTextValue;
    unit: WidgetUnits;
    versionPropertyId?: string;
}

export interface IWidgetElementDataNode extends IElementDataNode {
    readonly kind: NodeKind.Widget;
    html: string;
    js: string;
    ts: string;
    css: string;
    customProperties?: IWidgetCustomProperty[];
    parentId?: string;
    widgetReference?: string;
}

export interface IGroupElementDataNode extends IElementDataNode {
    readonly kind: NodeKind.Group;
    readonly elements: OneOfDataElements[];
}

export interface IAnyElementDataNode extends IElementDataNode {
    readonly kind: NodeKind.AnyElement;
}

export type OneOfDataElements =
    IRectangleElementDataNode |
    IEllipseElementDataNode |
    ITextElementDataNode |
    IButtonElementDataNode |
    IImageElementDataNode |
    IGroupElementDataNode |
    IWidgetElementDataNode |
    IAnyElementDataNode;

interface IAnimatedProperty {
    name: AnimatableProperty;
    keyframes: IKeyframe[];
}

export interface ITransition {
    id?: string;
    type: 'in' | 'out';
    name: string;
    timingFunction?: string;
    template?: string;
    settings?: { [key: string]: ITransitionSetting; };
    __time?: number;
}

export interface ITransitionTemplate extends ITransition {
    animatedProperties: IAnimatedProperty[];
}

export interface ITransitionSetting {
    name: string;
    value: any;
}

export const transitionSettingPropertyToUnitMap = {
    'direction': 'angle',
    'distance': 'number',
};

export interface IAnyElement extends IElementNode {
    readonly kind: NodeKind.AnyElement;
}

export function getElementKindAsString(element: OneOfElements): string {
    switch (element.kind) {
        case NodeKind.Text:
            return 'text';
        case NodeKind.Button:
            return 'button';
        case NodeKind.Rectangle:
            return 'rectangle';
        case NodeKind.Ellipse:
            return 'ellipse';
        case NodeKind.Image:
            return 'image';
        case NodeKind.Widget:
            return 'widget';
        case NodeKind.AnyElement:
            return 'any';
    }
    throw new Error('Unknown element kind');
}

export function getKindAsString(kind: NodeKind | 'feeds'): string {
    switch (kind) {
        case NodeKind.Text:
            return 'texts';
        case NodeKind.Button:
            return 'buttons';
        case NodeKind.Rectangle:
            return 'shapes';
        case NodeKind.Ellipse:
            return 'shapes';
        case NodeKind.Image:
            return 'images';
        case NodeKind.Widget:
            return 'widgets';
        case NodeKind.AnyElement:
            return 'elements';
        case 'feeds':
            return 'feeds';
        case NodeKind.Selection:
            return 'selection';
    }
    throw new Error('Unknown element kind');
}

export function getKindTitleAsString(kind: NodeKind | 'feeds'): string {
    switch (kind) {
        case NodeKind.Text:
            return 'Text Tool';
        case NodeKind.Button:
            return 'Button Tool';
        case NodeKind.Rectangle:
            return 'Rectangle Tool';
        case NodeKind.Ellipse:
            return 'Ellipse Tool';
        case NodeKind.Image:
            return 'Images';
        case NodeKind.Widget:
            return 'Widgets';
        case NodeKind.AnyElement:
            return 'Brand Library';
        case 'feeds':
            return 'Dynamic Content';
        case NodeKind.Selection:
            return '';
    }
    throw new Error('Unknown element kind');
}

export function getKindMessageAsString(kind: NodeKind | 'feeds'): string {
    switch (kind) {
        case NodeKind.Text:
            return 'Click/drag on canvas to\ncreate a new text area.';
        case NodeKind.Button:
            return 'Click/drag on canvas to\ncreate a new button.';
        case NodeKind.Rectangle:
            return 'Click/drag on canvas to\ncreate a new rectangle.';
        case NodeKind.Ellipse:
            return 'Click/drag on canvas to\ncreate a new ellipse.';
        case NodeKind.Image:
            return 'Store images that you\nwant to reuse across\ncreative sets here.';
        case NodeKind.Widget:
            return 'Click the plus button above\nto import a stock widget or\ncreate a new one from scratch.';
        case NodeKind.AnyElement:
            return 'Store elements that you\nwant to reuse across\ncreative sets in the library.';
        case 'feeds':
            return 'Dynamic variables\nwill show up here\nonce set up for your brand.';
        case NodeKind.Selection:
            return '';
    }
    throw new Error('Unknown element kind');
}

export function getSearchPlaceholderAsString(kind: NodeKind | 'feeds'): string {
    switch (kind) {
        case NodeKind.Text:
            return 'Search text elements';
        case NodeKind.Button:
            return 'Search buttons';
        case NodeKind.Rectangle:
            return 'Search rectangles';
        case NodeKind.Ellipse:
            return 'Search ellipses';
        case NodeKind.Image:
            return 'Search images';
        case NodeKind.Widget:
            return 'Search widgets';
        case NodeKind.AnyElement:
            return 'Search the library';
        case 'feeds':
            return 'Search dynamic content';
        default:
            return 'Search the library';
    }
}

export function getStringAsKind(type: string): NodeKind {
    switch (type) {
        case 'text':
            return NodeKind.Text;
        case 'button':
            return NodeKind.Button;
        case 'rectangle':
            return NodeKind.Rectangle;
        case 'ellipse':
            return NodeKind.Ellipse;
        case 'image':
            return NodeKind.Image;
        case 'widget':
        case 'bannerflowLibraryWidget':
        case 'bannerflowLibraryWidgetInstance':
            return NodeKind.Widget;
        case 'any':
            return NodeKind.AnyElement;
        case NodeKind.Selection:
            return NodeKind.Selection;
    }
    throw new Error('Unknown element kind');
}

export const characterPropertyToUnitMap = {
    'font': 'FontStyle',
    'textColor': 'Color',
    'fontSize': 'number',
    'lineHeight': 'number',
    'characterSpacing': 'number',
    'uppercase': 'boolean',
    'underline': 'boolean',
    'strikethrough': 'boolean',
    'textShadows': 'TextShadow[]',
    'variable': 'Feed',

    // A helper property to display, font family in dropdown, when font style is undefined.
    '__fontFamilyId': 'string',
};

export const textPropertiesOnlyToUnitMap = {
    'maxRows': 'integer',
    'padding': 'Padding',
    'textOverflow': 'enum',
    'horizontalAlignment': 'enum',
    'verticalAlignment': 'enum',
};

export const textPropertiesOnly = Object.keys(textPropertiesOnlyToUnitMap);

export const textPropertyToUnitMap = {
    ...omit(characterPropertyToUnitMap, 'variable'),
    ...textPropertiesOnlyToUnitMap
};

export const textProperties = Object.keys(textPropertyToUnitMap) as (keyof typeof textPropertyToUnitMap)[];
export const charecterProperties = Object.keys(characterPropertyToUnitMap);

export const widgetPropertyToUnitMap = {
    'js': 'string',
    'ts': 'string',
    'html': 'string',
    'css': 'string',
    'customProperties': 'CustomProperty[]',
    'widgetReference': 'string',
    'bannerflowLibraryWidgetReference': 'string',
    'select': 'CustomProperty[]',
    'number': 'number',
    'image': 'ImageAsset'
};

export const transitionPropertiesToUnitMap = {
    'inTransition': 'Transition',
    'outTransition': 'Transition',
    'duration': 'number'
};

export const propertyToUnitMap = {
    ...textPropertyToUnitMap,
    ...widgetPropertyToUnitMap, // TODO: Separate?
    ...transitionPropertiesToUnitMap,
    'x': 'number',
    'y': 'number',
    'width': 'number',
    'height': 'number',
    'content': 'text',
    'fill': 'Color',
    'color': 'Color',
    'originX': 'number',
    'originY': 'number',
    'rotationX': 'number',
    'rotationY': 'number',
    'rotationZ': 'number',
    'radius': 'number',
    'opacity': 'number',
    'shadows': 'Shadow[]',
    'border': 'Border',
    'imageReference': 'string',
    'imageAsset': 'ImageAsset',
    'widgetAsset': 'WidgetAsset',
    'fontReference': 'string',
    'hidden': 'boolean',
    'mirrorX': 'boolean',
    'mirrorY': 'boolean',
    'scaleX': 'number',
    'scaleY': 'number',
    'feed': 'Feed',
    'fitOption': 'enum',
    'fitPositionX': 'number',
    'fitPositionY': 'number',
    'quality': 'number'
};

export const properties = Object.keys(propertyToUnitMap);

export const defaultPropertyValues = {
    'x': 0,
    'y': 0,
    'width': 0,
    'height': 0,
    'fill': undefined,
    'rotationX': 0,
    'rotationY': 0,
    'rotationZ': 0,
    'originX': 50,
    'originY': 50,
    'mirrorX': false,
    'mirrorY': false,
    'radius': 0,
    'opacity': 1,
    'shadows': undefined,
    'border': undefined,
    'textColor': Color.parse('#000000'),
    'textShadows': undefined,
    'textDecoration': undefined,
    'uppercase': undefined,
    'lineHeight': undefined,
    'fontSize': 12,
    'maxRows': undefined,
    'padding': 0,
    'textOverflow': 'shrink',
    'characterSpacing': 0,
    'horizontalAlignment': 'center',
    'verticalAlignment': 'middle',
    'hidden': false,
    'scaleX': 1,
    'scaleY': 1
};

export const animatableProperties = stringLiteralArray([
    'x',
    'y',
    'originX',
    'originY',
    'rotationX',
    'rotationY',
    'rotationZ',
    'opacity',
    'scaleX',
    'scaleY'
]);
export type AnimatableProperty = typeof animatableProperties[number];

export function isTextNode(node?: OneOfSelectableElements | IElementNode | OneOfDataElements | IElementDataNode): node is ITextNode {
    return node !== undefined && (node!.kind === NodeKind.Button || node!.kind === NodeKind.Text);
}

export function isTextDataNode(node?: OneOfSelectableElements | IElementNode | OneOfDataElements | IElementDataNode): node is ITextDataNode {
    return node !== undefined && (node!.kind === NodeKind.Button || node!.kind === NodeKind.Text);
}

export function isTextElement(element: OneOfElements): element is OneOfTextNodes {
    return element.kind === NodeKind.Button || element.kind === NodeKind.Text;
}

export function isWidgetNode(node?: OneOfSelectableElements | IElementNode | OneOfDataElements): node is IWidgetElement {
    return node !== undefined && isWidgetKind(node.kind);
}

export function isWidgetKind(kind: NodeKind): boolean {
    return kind === NodeKind.Widget
        || kind === NodeKind.BannerflowLibraryWidget
        || kind === NodeKind.BannerflowLibraryWidgetInstance;
}

export function isWidgetElement(node?: IElement): boolean {
    return node !== undefined && (node!.type === NodeKind.Widget || node.type === NodeKind.BannerflowLibraryWidget || node!.type === NodeKind.BannerflowLibraryWidgetInstance);
}

export function isElementNode(node?: OneOfSelectableElements | IElementNode | ISVGBackgroundNode): node is IElementNode {
    return (
        node !== undefined &&
        node.kind !== NodeKind.Creative &&
        node.kind !== NodeKind.AnyElement &&
        node.kind !== NodeKind.Selection
    );

}

export function isCreativeViewNode(node?: OneOfSelectableElements | IElementNode | ICreativeViewNode): node is ICreativeViewNode {
    return node !== undefined && node!.kind === NodeKind.Creative;
}

export function isElementVisibleAtTime(element: OneOfElements | OneOfDataElements | ElementSelection, time: number): boolean {
    const hidden = element.kind === NodeKind.Selection ? false : element.hidden;

    return !hidden && time >= element.time
        && time <= (element.time + element.duration);
}

export interface IChangeEvent {
    property: OneOfElementPropertyKeys | 'x' | 'y' | 'width' | 'height' | 'constraint' | 'ratio';
    value: any;
}

export type ChangeEventCallback = (element: IElementDataNode, change: IChangeEvent) => void;

interface IRootNode {
    readonly kind: NodeKind.Creative;
    elements: OneOfDataElements[];
}

export interface ICreativeViewNode extends ISVGBackgroundNode, ISize {
    readonly kind: NodeKind.Creative;
    readonly elements: OneOfElements[];
}

export interface ICreativeDataNode {
    id: string;
    kind: NodeKind.Creative;
    fill?: IColor;
    border?: IBorder;
    radius: number;
    width: number;
    height: number;
    elements: OneOfDataElements[];
    guidelines?: IGuideline[];
}

export interface ICreativeInput {
    id?: string;
    width: number;
    height: number;
    fill?: Color;
    loops?: number;
    radius?: number;
    startTime?: number;
    stopTime?: number;
    guidelines?: IGuideline[];
    preloadImage?: IPreloadImage;
}

export class CreativeDataNode implements ISize, IRootNode {
    readonly kind: NodeKind.Creative = NodeKind.Creative;
    elements: OneOfDataElements[] = [];
    id: string;
    fill: IColor;
    radius: number;
    loops = 0;
    stopTime?: number;
    startTime?: number;
    width: number;
    height: number;
    noEmit = false;
    callbacks: ChangeEventCallback[] = [];
    guidelines: IGuideline[] = [];
    preloadImage: IPreloadImage;

    get duration(): number {
        let latestAnimationEnd = 0;
        for (const element of this.elements) {
            if (element.time + element.duration > latestAnimationEnd) {
                latestAnimationEnd = element.time + element.duration;
            }
        }
        // JS rounds strangely sometimes: 1.77 + 2.34 = 4.109999999999999
        return Math.round(latestAnimationEnd * 100) / 100;
    }

    static copy(creative: CreativeDataNode): CreativeDataNode {
        const copy = new CreativeDataNode(creative);
        copy.id = uuidv4();
        copy.elements = creative.elements;
        return copy;
    }

    constructor(creative: ICreativeInput) {
        this.kind = NodeKind.Creative;
        this.id = creative.id || uuidv4();
        this.width = creative.width;
        this.height = creative.height;
        this.fill = creative.fill || Color.fromRGBA('rgba(255, 255, 255, 1)');
        this.radius = creative.radius || 0;
        this.loops = creative.loops || 0;
        this.startTime = creative.startTime;
        this.stopTime = creative.stopTime;
        this.guidelines = creative.guidelines || [];
        this.preloadImage = {
            quality: 70,
            format: 'jpg',
            frames: [],
            ...creative.preloadImage
        };
    }

    destroy(): void {
        this.callbacks = [];
    }

    getFirstPreloadImageFrame(): number {
        if (this.preloadImage.frames.length > 0) {
            return this.preloadImage.frames[0];
        }
        return 1;
    }

    getStopTime(): number {
        if (typeof this.stopTime === 'number') {
            return this.stopTime;
        }
        else {
            return this.duration - 1;
        }
    }

    subscribe(callback: ChangeEventCallback): void {
        this.callbacks.push(callback);
    }

    unsubscribe(callback?: ChangeEventCallback): void {
        if (!callback) {
            this.callbacks = [];
            return;
        }
        const index = this.callbacks.indexOf(callback);
        if (index !== -1) {
            this.callbacks.splice(index, 1);
        }
    }

    *elementIterator(group?: OneOfGroupDataNode): IterableIterator<OneOfDataElements> {
        if (!group) {
            group = this;
        }
        switch (group.kind) {
            case NodeKind.Creative:
            case NodeKind.Group:
                for (const element of group.elements) {
                    switch (element.kind) {
                        case NodeKind.Group:
                            yield* this.elementIterator(element);
                            break;
                        default:
                            yield element;
                    }
                }
                break;
            default:
                throw new Error('Unknown group element.');
        }
    }
}

type SelectionEvents = 'change' | 'layoutchange';
export class ElementSelection extends EventEmitter<SelectionEvents> implements IPosition, ISize {
    readonly kind = NodeKind.Selection;
    x: number;
    y: number;
    width: number;
    height: number;
    noEmit: boolean;
    locked: boolean;

    // Animation box
    time: number;
    duration: number;

    __boundingBox?: IBoundingBox;

    canvasSize: ISize;

    originalElements = new Set<OneOfElements>();
    elementsToBeRemoved = new Set<OneOfElements>();
    elementsToBeAdded = new Set<OneOfElements>();
    storedElementsToBeRemoved = new Set<OneOfElements>();
    storedElementsToBeAdded = new Set<OneOfElements>();

    constructor(elements: OneOfElements[] = [], canvasSize: ISize) {
        super();

        this.canvasSize = canvasSize;

        for (const element of elements) {
            this.originalElements.add(element);
        }
        this.updateBoundingBoxes();
    }

    get rotationZ(): number {
        return (this.element && this.element.rotationZ) || 0;
    }
    // Don't do anything when trying to set rotation
    set rotationZ(val: number) {

    }

    getEarliestAnimationTime(): number {
        let earliest = 9999;
        for (const element of this.elements) {
            if (element.time < earliest) {
                earliest = element.time;
            }
        }
        return earliest;
    }

    updateBoundingBoxes(): void {
        this.calculateBoundingBox();
        this.calculateAnimationBoundingBox();
    }

    calculateCurrentBoundingBox(): void {

        const bounds = getOuterBounds(...this.elements);

        // Only emit if changed
        if (!isSameBounds(this, bounds)) {
            this.x = bounds.x;
            this.y = bounds.y;
            this.width = bounds.width;
            this.height = bounds.height;
            this.emit('layoutchange');
        }
    }

    getBoundingBox(): IBoundingBox | undefined {
        let left = Infinity;
        let top = Infinity;
        let right = -Infinity;
        let bottom = -Infinity;

        if (!this.canvasSize) {
            return;
        }

        for (const element of this.elements) {
            const data = element.__data;
            if (data) {
                let boundingBox = element.__boundingBox = getMainBoundingBox(data as OneOfDataElements);
                if (this.elements.size > 1) {
                    boundingBox = getBoundingRectangleOfRotatedBox(boundingBox);
                }

                left = Math.min(boundingBox.x, left);
                top = Math.min(boundingBox.y, top);
                right = Math.max(boundingBox.x + boundingBox.width, right);
                bottom = Math.max(boundingBox.y + boundingBox.height, bottom);
            }
        }

        return {
            x: left,
            y: top,
            width: right - left,
            height: bottom - top,
            rotationZ: this.rotationZ
        };
    }

    calculateBoundingBox(): IBoundingBox | undefined {
        const boundingBox = this.getBoundingBox();
        this.__boundingBox = boundingBox;


        if (boundingBox) {
            Object.assign(this, boundingBox);
        }

        this.emit('layoutchange');

        return boundingBox;
    }

    calculateAnimationBoundingBox(): void {
        let start = Infinity;
        let stop = -Infinity;
        for (const element of this.elements) {
            start = Math.min(element.time, start);
            stop = Math.max(element.time + element.duration, stop);
        }

        this.time = start;
        this.duration = stop - start;
    }

    add(elements: OneOfElements | OneOfElements[]): void {
        this.noEmit = true;
        if (Array.isArray(elements)) {
            for (const element of elements) {
                if (element.locked) {
                    continue;
                }
                this.elementsToBeAdded.add(element);
                this.elementsToBeRemoved.delete(element);
            }
        }
        else {
            this.elementsToBeAdded.add(elements);
            this.elementsToBeRemoved.delete(elements);
        }
        this.noEmit = false;
        this.updateBoundingBoxes();
        this.emit('change');
    }

    delete(elements: OneOfElements | OneOfElements[]): void {
        this.noEmit = true;
        if (Array.isArray(elements)) {
            for (const element of elements) {
                this.elementsToBeRemoved.add(element);
                this.elementsToBeAdded.delete(element);
            }
        }
        else {
            this.elementsToBeRemoved.add(elements);
            this.elementsToBeAdded.delete(elements);
        }
        this.noEmit = false;
        this.updateBoundingBoxes();
        this.emit('change');
    }

    getElementIds(): string[] {
        const elementIds: string[] = [];
        for (const element of this.elements) {
            elementIds.push(element.id);
        }
        return elementIds;
    }

    use(selection: ElementSelection): void {
        this.originalElements.clear();
        this.elementsToBeRemoved.clear();
        this.elementsToBeAdded.clear();
        this.storedElementsToBeRemoved.clear();
        this.storedElementsToBeAdded.clear();

        this.copySet(selection.originalElements, this.originalElements);
        this.copySet(selection.elementsToBeRemoved, this.elementsToBeRemoved);
        this.copySet(selection.elementsToBeAdded, this.elementsToBeAdded);
        this.copySet(selection.storedElementsToBeRemoved, this.storedElementsToBeRemoved);
        this.copySet(selection.storedElementsToBeAdded, this.storedElementsToBeAdded);
        this.updateBoundingBoxes();
        this.emit('change');
    }

    clear(): void {
        this.noEmit = true;
        this.elementsToBeAdded.clear();
        for (const originalElement of this.originalElements) {
            if (originalElement) {
                this.elementsToBeRemoved.add(originalElement);
            }
        }
        this.updateBoundingBoxes();
        this.noEmit = false;
        this.emit('change');
    }

    get element(): OneOfElements | undefined {
        if (this.elements.size !== 1) {
            return undefined;
        }
        return this.elements.values().next().value;
    }

    set(element: OneOfElements): void {
        this.noEmit = true;
        this.elementsToBeAdded.clear();
        this.elementsToBeRemoved.clear();
        let containsElement = false;
        for (const originalElement of this.originalElements) {
            if (element !== originalElement) {
                this.elementsToBeRemoved.add(originalElement);
            }
            else {
                containsElement = true;
            }
        }
        if (!containsElement) {
            this.elementsToBeAdded.add(element);
        }
        this.updateBoundingBoxes();
        this.noEmit = false;
        this.emit('change');
    }

    get elements(): Set<OneOfElements> {
        const elements = new Set(this.originalElements);
        for (const element of this.elementsToBeRemoved) {
            elements.delete(element);
        }
        for (const element of this.elementsToBeAdded) {
            elements.add(element);
        }
        return elements;
    }

    asSortedArray(): OneOfElements[] {
        return Array.from(this.elements).sort((a: OneOfElements, b: OneOfElements) => {
            return getElementIndex(a) - getElementIndex(b);
        });
    }

    asArray(): OneOfElements[] {
        return Array.from(this.elements);
    }

    save(): void {
        this.copySet(this.elementsToBeAdded, this.storedElementsToBeAdded);
        this.copySet(this.elementsToBeRemoved, this.storedElementsToBeRemoved);
    }

    restore(): void {
        this.copySet(this.storedElementsToBeAdded, this.elementsToBeAdded);
        this.copySet(this.storedElementsToBeRemoved, this.elementsToBeRemoved);
        this.updateBoundingBoxes();
    }

    commit(): void {
        this.originalElements = new Set(this.elements);
        this.elementsToBeAdded.clear();
        this.elementsToBeRemoved.clear();
        this.storedElementsToBeAdded.clear();
        this.storedElementsToBeRemoved.clear();
    }

    has(element: OneOfElements): boolean {
        return this.elements.has(element);
    }

    clone(): ElementSelection {
        const selection = new ElementSelection(undefined, this.canvasSize);
        this.copySet(this.originalElements, selection.originalElements);
        this.copySet(this.elementsToBeRemoved, selection.elementsToBeRemoved);
        this.copySet(this.elementsToBeAdded, selection.elementsToBeAdded);
        this.copySet(this.storedElementsToBeRemoved, selection.storedElementsToBeRemoved);
        this.copySet(this.storedElementsToBeAdded, selection.storedElementsToBeAdded);

        return selection;
    }

    get length(): number {
        return this.elements.size;
    }

    private copySet<T extends Set<any>>(source: T, target: T): void {
        target.clear();
        for (const s of source) {
            target.add(s);
        }
    }
}

export interface IGroupElement extends IElementNode, IChildren {
    readonly kind: NodeKind.Group;
}

/**
 * Properties that can be changed by character styling
 */
export interface ICharacterProperties {
    textColor?: Color;
    fontSize?: number;
    font?: IFontStyle;
    uppercase?: boolean;
    underline?: boolean;
    strikethrough?: boolean;
    characterSpacing: number;
    lineHeight: number;
    textShadows?: ITextShadow[];
    variable?: T.IVariable;
    __fontFamilyId?: string;
}

export interface IMixedCharacterProperties {
    textColor?: Color;
    fontSize?: number;
    font?: IFontStyle | '$mixed';
    uppercase: boolean;
    underline: boolean;
    strikethrough: boolean;
    characterSpacing: number;
    lineHeight: number;
    textShadows?: ITextShadow[];
    variable?: IFeed;
    __fontFamilyId: string | '$mixed';
}

export interface ITextElementProperties {
    horizontalAlignment: T.HorizontalAlignment;
    verticalAlignment: T.VerticalAlignment;
    maxRows: number;
    padding: IPadding;
    textOverflow: T.TextOverflow;
}

export type ITextProperties = ITextElementProperties & ICharacterProperties;

export function createDefaultTextProperties(): ITextProperties {
    const properties: ITextProperties = {
        padding: { top: 0, left: 0, right: 0, bottom: 0 },
        horizontalAlignment: 'left',
        verticalAlignment: 'top',
        maxRows: Infinity,
        textOverflow: 'expand',
        characterSpacing: 0,
        lineHeight: 1.3,
        __fontFamilyId: '',
    };
    return properties;
}
/**
 * Shared interface for text and buttons (currently identical)
 */
export interface ITextNode extends T.ITextElementProperties, T.ICharacterProperties, IElementNode {
    content: T.IText;
    horizontalAlignment: T.HorizontalAlignment;
    verticalAlignment: T.VerticalAlignment;
    maxRows: number;
    padding: IPadding;
    textOverflow: T.TextOverflow;
    __rootElement?: HTMLDivElement;
    __centerElement?: HTMLDivElement;
    __textElement?: HTMLDivElement;
    __richTextRenderer: RichText;
}

export interface ITextDataNode extends IElementDataNode {
    content: T.IText;
    textColor: IKeyframe[];
    textShadows?: IKeyframe[];
    uppercase: IKeyframe[];
    underline: IKeyframe[];
    strikethrough: IKeyframe[];
    horizontalAlignment: IKeyframe[];
    verticalAlignment: IKeyframe[];
    lineHeight: IKeyframe[];
    characterSpacing: IKeyframe[];
    fontSize?: IKeyframe[];
    maxRows?: IKeyframe[];
    padding?: IKeyframe[];
    textOverflow?: IKeyframe[];
    font: IFontStyle;
    characterStyles: Map<string, Partial<T.ICharacterProperties>>;
    __styleHashMap: Map</*styleHash*/string, /*styleId*/string>;

    // We have to store the deleted style's ids to refer to them if we happen to create new style
    // with exact same styles.
    __deletedStyleHashMap: Map</*styleHash*/string, /*styleId*/ string>;
    __fontFamilies: IFontFamilies;
    __fontStyleId?: string;
    __rootElement?: HTMLDivElement;
    __backgroundElement?: SVGSVGElement;
    __centerElement?: HTMLDivElement;
    __textElement?: HTMLDivElement;
}

export interface IStyleEntry {
    styleId: string;
    style: Partial<T.ICharacterProperties>;
}

export interface ITextElement extends ITextNode {
    readonly kind: NodeKind.Text;
}

export interface IButtonElement extends ITextNode {
    readonly kind: NodeKind.Button;
}

export interface IEllipseElement extends IElementNode {
    readonly kind: NodeKind.Ellipse;
}

export interface IRectangleElement extends IElementNode {
    readonly kind: NodeKind.Rectangle;
}

export interface IImageElement extends IElementNode {
    readonly kind: NodeKind.Image;
    imageAsset?: IImageAsset;
    feed?: IFeed;
    parentId?: string;
    fitOption?: FitOption;
    fitPositionX?: number;
    fitPositionY?: number;
    quality?: number;
}

export type FitOption = 'contain' | 'distort' | 'cover';

export interface IWidgetElement extends IElementNode {
    readonly kind: NodeKind.Widget;
    widgetAsset: IWidgetAsset;
    html: string;
    js: string;
    css: string;
    customProperties?: IWidgetCustomProperty[];
    parentId?: string;
}

export type WidgetPropertyKeys = keyof typeof widgetPropertyToUnitMap;
export type ElementPropertyMap = typeof propertyToUnitMap;
export type OneOfElementPropertyKeys = keyof ElementPropertyMap;

// export interface IElementProperty {
//     type: ElementPropertyKeys;
//     unit: string;
//     value?: string;
//     versionPropertyId?: string;
// }

const characters = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z'];
let currentCidIndex = 1;

export function createElementCid(): string {
    const position = currentCidIndex++;
    return getNumberSystemEncoding(position);
}

export function getNumberSystemEncoding(position: number): string {
    const numberSystem = characters.length;
    if (position <= numberSystem) {
        return characters[position - 1];
    }
    const numerals: string[] = [];
    let currentNumber = position;
    while (currentNumber >= numberSystem) {
        const remainder = currentNumber % numberSystem;
        currentNumber = (currentNumber - remainder) / numberSystem;
        numerals.push(characters[remainder]);
    }
    numerals.push(characters[currentNumber - 1]);
    return numerals.reverse().join('');
}

export function incrementElementCid(): void {
    currentCidIndex++;
}

export function resetNodeCid(): void {
    currentCidIndex = 1;
}

export function getCurrentCidIndex(): number {
    return currentCidIndex;
}

export function convertCidToIndex(cid: string): number {
    let value = 0;
    for (let i = 0; i < cid.length; i++) {
        const index = characters.indexOf(cid[i]);
        value = value * characters.length + index;
    }

    return value + 1;
}

export function forEachElement(group: OneOfGroupNode, callback: (node: OneOfElements) => void, atTime?: number, reverse?: boolean): void {
    switch (group.kind) {
        case NodeKind.Creative:
        case NodeKind.Group:
            for (const element of (reverse ? group.elements.slice().reverse() : group.elements)) {
                if (typeof atTime !== 'number' || isElementVisibleAtTime(element, atTime)) {
                    switch (element.kind) {
                        case NodeKind.Group:
                            forEachElement(element, callback);
                            break;
                        default:
                            callback(element);
                    }
                }
            }
            break;
        default:
            throw new Error('Unknown group element.');
    }
}

export function getElementDataById<T extends OneOfDataElements>(id: string, document: OneOfGroupDataNode): T | undefined {
    let element: T | undefined;
    forEachDataElement(document, (e) => {
        if (e.id === id) {
            element = e as any; // TypeScript Bug;
        }
    });
    return element;
}

export function forEachDataElement(group: OneOfGroupDataNode, callback: (element: OneOfDataElements) => void, reverse?: boolean): void {
    switch (group.kind) {
        case NodeKind.Creative:
        case NodeKind.Group:
            for (const element of (reverse ? group.elements.slice().reverse() : group.elements)) {
                switch (element.kind) {
                    case NodeKind.Group:
                        forEachDataElement(element, callback);
                        break;
                    default:
                        callback(element);
                }
            }
            break;
        default:
            throw new Error('Unknown group element.');
    }
}

export function findElementByPredicate<T>(node: OneOfGroupNode, predicate: (element: OneOfElements) => T | undefined): T | undefined {
    switch (node.kind) {
        case NodeKind.Creative:
        case NodeKind.Group:
            for (const element of node.elements) {
                switch (element.kind) {
                    case NodeKind.Group: {
                        const e = findElementByPredicate(element, predicate);
                        if (e) {
                            return e;
                        }
                        break;
                    }
                    default: {
                        const e = predicate(element);
                        if (e) {
                            return e;
                        }
                    }
                }
            }
            break;
        default:
            const e = predicate(node);
            if (e) {
                return e;
            }
    }
}

export function setKeyframeValueOnDataElement(element: OneOfDataElements, property: OneOfElementPropertyKeys, value: any): void {
    if (typeof value === 'undefined') {
        element[property] = undefined;
    }
    else {
        if (element[property]) {
            const mainKeyframes = getMainPropertyKeyframesOnElement(element, property);
            if (mainKeyframes.length) {
                mainKeyframes.forEach(k => k.value = value);
            }
        }
        else {
            element[property] = [{
                value: value,
                time: 0
            }];
        }
    }
}

export function setKeyframeValueOnViewElement(element: OneOfElements, property: OneOfElementPropertyKeys, value: any): void {
    setKeyframeValueOnDataElement(element.__data as OneOfDataElements, property, value);
}

export function insertAfterElement(group: OneOfGroupNode, after: OneOfElements, element: OneOfElements): void {
    switch (group.kind) {
        case NodeKind.Creative:
        case NodeKind.Group:
            for (const [index, child] of group.elements.entries()) {
                switch (child.kind) {
                    case NodeKind.Group: {
                        insertAfterElement(child, after, element);
                        break;
                    }
                    default:
                        if (after === child) {
                            group.elements.splice(index, 0, element);
                            return;
                        }
                }
            }
            break;
        default:
            throw new Error('Unknown group element.');
    }
}

export function getRatioType(size: ISize): 'rectangle' | 'landscape' | 'skyscraper' {
    const ratio = size.width / size.height;

    if (ratio > 0.7 && ratio < 1.3) {
        return 'rectangle';
    }
    else if (ratio >= 1.3) {
        return 'landscape';
    }
    else {
        return 'skyscraper';
    }
}

export function positionIsInBounds(position: IPosition, bounds: IBounds | IBoundingBox): boolean {
    if (position.x >= bounds.x && position.x <= bounds.x + bounds.width) {
        if (position.y >= bounds.y && position.y <= bounds.y + bounds.height) {
            return true;
        }
    }
    return false;
}

export function getTwoSurroundingKeyframes(keyframes: IKeyframe[], time: number): IKeyframe[] {
    let result: IKeyframe[] = [];
    keyframes.some(keyframe => {
        if (keyframe.time > time) {
            result.push(keyframe);
            return true;
        }
        result = [keyframe];

        return keyframe.time > time;
    });

    return result;
}
export function isNumberBetween(origin: number, num2: number, margin: number = 5): boolean {
    return (origin - num2 - margin) * (origin - num2 + margin) <= 0;
}

export function getAnimatablePropertyValue(element: OneOfDataElements, property: OneOfElementPropertyKeys, time: number, canvasSize: ISize): any {
    // Avoid variable assignments here as it allocates a lot of memory due to very frequent usage (causes Major GC)
    if (element[property] && element[property].length) {
        if (element[property].length === 1 || (time < element.time || time > element.time + element.duration)) {
            return extractKeyframeValue(element[property][0], element, canvasSize);
        } else {
            const surroundingKeyframes = getTwoSurroundingKeyframes(element[property], time - element.time);
            if (surroundingKeyframes.length === 2) {
                const unit = propertyToUnitMap[property];

                switch (unit) {
                    case 'number':
                        const startTime = element.time + surroundingKeyframes[0].time;
                        const endTime = element.time + surroundingKeyframes[1].time;
                        const timingFunction =
                            surroundingKeyframes[1].timingFunction
                                ? timingFunctions[surroundingKeyframes[1].timingFunction].func
                                : timingFunctions.linear.func;

                        const t = (time - startTime) / (endTime - startTime);
                        const fraction = timingFunction(t);
                        return lerp(
                            +extractKeyframeValue(surroundingKeyframes[0], element, canvasSize)
                            , +extractKeyframeValue(surroundingKeyframes[1], element, canvasSize)
                            , !isNaN(fraction) ? fraction : 0
                        );
                    default:
                        return extractKeyframeValue(element[property][0], element, canvasSize);
                }
            }
            else if (surroundingKeyframes.length === 1) {
                return extractKeyframeValue(surroundingKeyframes[0], element, canvasSize);
            }
            else {
                return defaultPropertyValues[property];
            }
        }
    } else {
        return defaultPropertyValues[property];
    }
}

export function getElementTextProperties(data: OneOfTextDataNodes): Partial<T.ICharacterProperties> {
    const style: Partial<T.ICharacterProperties> = {};
    for (const characterProperty of characterProperties) {
        if (characterProperty === 'font') {
            style.font = data.font;
            continue;
        }
        if (data[characterProperty]) {
            style[characterProperty] = getAnimatablePropertyValue(data, characterProperty, 0, { width: 0, height: 0 });
        }
    }
    return style;
}

function getEdgePointBasedOnAngle(position: IPosition, elementSize: ISize, angle: number, canvasSize: ISize): IPosition {
    angle = (angle - 90) * (Math.PI / 180);
    const roundTo = 0.00001;
    const dx = roundToNearestMultiple(Math.cos(angle), roundTo);
    const dy = roundToNearestMultiple(Math.sin(angle), roundTo);
    const d = dy / dx;
    const vertical = dx === 0;
    const horizontal = dy === 0;

    // Left border
    if (dx < 1.0e-16 && !vertical) {
        const y = (position.x) * d + (canvasSize.height - position.y);
        if (y >= -elementSize.height && y <= canvasSize.height + elementSize.height) {
            return {
                x: -elementSize.width,
                y: canvasSize.height - y - elementSize.height * -(dy * 0.5)
            };
        }
    }

    // Right border
    if (dx > 1.0e-16 && !vertical) {
        const y = (canvasSize.width - position.x) * d + position.y;
        if (y >= -elementSize.height && y <= canvasSize.height + elementSize.height) {
            return {
                x: canvasSize.width,
                y: y + elementSize.height * (dy * 0.5)
            };
        }
    }

    // Top border
    if (dy < 1.0e-16 && !horizontal) {
        const x = (position.y) * dx / dy + (canvasSize.width - position.x);

        if (x >= -elementSize.width && x <= canvasSize.width + elementSize.width) {
            return {
                x: canvasSize.width - x - elementSize.width * -(dx * 0.5),
                y: -elementSize.height
            };
        }
    }

    // Bottom border
    if (dy > 1.0e-16 && !horizontal) {
        const x = (canvasSize.height - position.y) * dx / dy + position.x;
        if (x >= -elementSize.width && x <= canvasSize.width + elementSize.width) {
            return {
                x: x + elementSize.width * (dx * 0.5),
                y: canvasSize.height
            };
        }
    }

    return position;
}

let iframe: HTMLIFrameElement | undefined;
let isSandboxed = false; // Checks whether in sandboxed(i.e <iframe sandbox>) environment

// Needs to be string evaluated to get around 'use strict' restriction, because we have to use with-statements.
const evalMathExpressionFunctionBody = `
    context.eval = eval;
    context.code = code;
    const scopeProxy = new Proxy(context, {
        has(prop) { return !(prop in context) },
        get(target, prop) {
            if (prop in target) {
                return target[prop];
            }
            if (!(typeof prop === 'symbol')) {
                throw new Error(\`Unexpected token '\${prop.toString()\}'.\`);
            }
        }
    });
    with (scopeProxy) {
        return eval(code);
    }`;

const evalMathExpression = new Function('code', 'context', evalMathExpressionFunctionBody);

function safeEval(code: string, _context: any): number {
    if (!iframe) {
        initSafeEvalMathExpression();
    }
    if (isSandboxed) {
        return evalMathExpression(code, _context);
    }
    const contentWindow = iframe && (iframe.contentWindow as any);
    if (!contentWindow.evalMathExpression) {
        throw new Error('Safe eval iframe is not initiated.');
    }
    return contentWindow.evalMathExpression(code, _context);
}

/**
 * @summary
 * Initialize safe eval function for evaluating animation expressions.
 *
 * @description
 * This function will determine whether to use a stricter iframe sandboxed approach.
 * The stricter approach is mainly for safe-gaurding access to global state in the Studio client,
 * but it's used whenever it can, even in published ads. The "looser" approach is used only when
 * we are in a sandboxed(i.e. <iframe sandbox>) environment.
 *
 * Note, an iframe is needed because '({}).constructor === Object' and you can from there access
 * all global state. An iframe creates a copy of all global objects including '({}).constructor'
 * that is different from Studio client's. Thus, safe-guarding us from acessing Studio client's
 * global state.
 *
 * Note, all published creatives will either be wrapped in an iframe or not. When they are
 * wrapped in an iframe, they are safe. Published ads served through sandboxed iframes prohibits
 * us from creating additional iframes. We don't need the iframe or use the stricter approach,
 * since we already are inside an iframe.
 */
function initSafeEvalMathExpression(): void {
    if (!iframe && typeof window !== 'undefined') {
        iframe = document.createElement('iframe');
        iframe.style.width = '0px';
        iframe.style.height = '0px';
        iframe.style.opacity = '0';
        iframe.addEventListener('load', () => {
            const iframeDoc = iframe!.contentDocument!;

            // In sandboxed environment contentDocument is null.
            if (!iframeDoc) {
                isSandboxed = true;
                return;
            }
            const process = (window as any).process;
            const STUDIO_JS = typeof process !== undefined && process && process.env && process.env.STUDIO_JS;
            const nonce: string | undefined = STUDIO_JS ? `nonce="${getCookie('script-nonce')}"` : '';
            iframeDoc.open();
            iframeDoc.write(`<script ${nonce}>window.evalMathExpression = ${evalMathExpression.toString()}</script>`);
            iframeDoc.close();
        });
        document.body.appendChild(iframe);
    }
}
initSafeEvalMathExpression();

export function extractKeyframeValue(keyframe: IKeyframe, element: OneOfDataElements, canvasSize: ISize): any {
    const value = keyframe.value;

    // Value is a formula
    if (value
        && typeof value === 'string'
        && value.indexOf('@') >= 0
        && value !== '@value') {

        const x = +getMainPropertyKeyframesOnElement(element, 'x')[0].value;
        const y = +getMainPropertyKeyframesOnElement(element, 'y')[0].value;
        const width = +getMainPropertyKeyframesOnElement(element, 'width')[0].value;
        const height = +getMainPropertyKeyframesOnElement(element, 'height')[0].value;
        const rotationX = +getMainPropertyKeyframesOnElement(element, 'rotationX')[0].value;
        const rotationY = +getMainPropertyKeyframesOnElement(element, 'rotationY')[0].value;
        const rotationZ = +getMainPropertyKeyframesOnElement(element, 'rotationZ')[0].value;

        const boundingBox = getBoundingRectangleOfRotatedBox({
            x: 0,
            y: 0,
            width: width,
            height: height,
            rotationZ: rotationZ
        });
        // Exposed properties and method
        const props: any = {
            x,
            y,
            rotationX,
            rotationY,
            rotationZ,
            width: boundingBox.width,
            height: boundingBox.height,
            creativeWidth: canvasSize.width,
            creativeHeight: canvasSize.height,
            edgePoint: (angle: number) => {
                return getEdgePointBasedOnAngle(
                    {
                        x,
                        y
                    },
                    {
                        width: boundingBox.width,
                        height: boundingBox.height
                    },
                    angle,
                    canvasSize
                );
            },
            transition: {}
        };

        // Let us use "@value" inside the transition formula
        if (keyframe.property) {
            props.value = props[keyframe.property];
        }

        if (keyframe.transition && keyframe.transition.settings) {
            for (const key in keyframe.transition.settings) {
                props.transition[key] = keyframe.transition.settings[key].value;
            }
        }

        const expression = value.replace(/\@/g, '');
        const parsedValue = safeEval(expression, { ...props, sin: Math.sin, cos: Math.cos, pi: Math.PI, abs: Math.abs });

        return parsedValue;
    } else {
        return value;
    }
}

export function getMainPropertyKeyframesOnElement(element: OneOfDataElements, property: OneOfElementPropertyKeys, createIfMissing: boolean = true): IKeyframe[] {
    const transitions = getTransitionKeyframesOnElement(element, property);

    if (transitions !== undefined && (transitions.in.length > 0 || transitions.out.length > 0)) {
        return [
            ...(transitions.in.length ? [transitions.in[transitions.in.length - 1]] : []),
            ...(transitions.out.length ? [transitions.out[0]] : [])
        ];
    }
    else {
        if (!element[property]) {
            if (createIfMissing) {
                element[property] = [{
                    time: 0,
                    value: defaultPropertyValues[property]
                }];
                return [element[property][0]];
            }
            else {
                return [{
                    time: 0,
                    value: defaultPropertyValues[property]
                }];
            }
        }
        else {
            return [element[property][0]];
        }

    }
}

/**
 * Get bounding box of main state (trnaisitions exluded)
 * @param element
 */
export function getMainBoundingBox(element: OneOfDataElements): IBoundingBox {

    const width = +getMainPropertyValue(element as OneOfDataElements, 'width');
    const height = +getMainPropertyValue(element as OneOfDataElements, 'height');
    const x = +getMainPropertyValue(element as OneOfDataElements, 'x');
    const y = +getMainPropertyValue(element as OneOfDataElements, 'y');
    const rotationZ = +getMainPropertyValue(element as OneOfDataElements, 'rotationZ');
    return { x, y, width, height, rotationZ };
}

export function setBoundingBox(element: OneOfElements | ElementSelection, canvasSize: ISize): IBoundingBox | undefined {
    if (element.kind === NodeKind.Selection) {
        return element.calculateBoundingBox();
    }
    else {
        const data = element.__data;
        if (data) {
            return element.__boundingBox = getMainBoundingBox(data as OneOfDataElements);
        }
    }
}

export function getMainPropertyValue(element: OneOfDataElements, property: OneOfElementPropertyKeys): any {
    return element[property]
        ? getMainPropertyKeyframesOnElement(element, property)[0].value
        : defaultPropertyValues[property];
}

export function getAllKeyframesOnElement(element: OneOfDataElements): IKeyframe[] {
    const keyframes: IKeyframe[] = [];
    for (const prop of animatableProperties) {
        if (element[prop]) {
            keyframes.push(...element[prop]!);
        }
    }

    return keyframes;
}

export function getKeyframesAtTime(time: number, element: OneOfDataElements, tolerance: number = 0): IKeyframe[] {
    const keyframes: IKeyframe[] = [];
    for (const prop of animatableProperties) {
        if (element[prop]) {
            for (const keyframe of element[prop]!) {
                const t = element.time + keyframe.time;
                if (time > t - tolerance && time < t + tolerance) {
                    keyframes.push(keyframe);
                }
            }
        }
    }

    return keyframes;
}

export function createAnimatableValue(value: any): IKeyframe[] {
    return [{
        time: 0,
        value: value
    }];
}

export function getElementIndex(element: OneOfElements): number {
    // TODO: find reference issue, shouldn't have to compare ids here...
    return element.__data!.__rootNode!.elements.findIndex(e => e.id === element.id);
}

export function getElementIdentifier(elementOrSelection: OneOfSelectableElements): string {
    if (elementOrSelection.kind === NodeKind.Selection) {
        if (elementOrSelection.length === 1) {
            const element = elementOrSelection.element!;
            return `${element.kind}:${element.elementCid}:${element.id}`;
        }
        else {
            return `${elementOrSelection.kind}:${elementOrSelection.length}`;
        }
    }
    else {
        const element = elementOrSelection;
        return `${element.kind}:${element.elementCid}:${element.id}`;
    }
}

export function createViewElement(element: OneOfDataElements): any {
    const viewElement = { kind: element.kind, __data: element } as OneOfElements;
    viewElement.elementCid = createElementCid();
    viewElement.cidIndex = getCurrentCidIndex();
    viewElement.id = element.id;
    viewElement.name = element.name;
    viewElement.ratio = element.ratio;
    viewElement.locked = element.locked;
    viewElement.hidden = element.hidden;
    viewElement.inTransition = element.inTransition;
    viewElement.outTransition = element.outTransition;

    const transitionKeyframes = getTransitionKeyframesOnElement(element);
    if (viewElement.inTransition && transitionKeyframes.in.length) {
        viewElement.inTransition.__time = transitionKeyframes.in[transitionKeyframes.in.length - 1].time;
    }
    if (viewElement.outTransition && transitionKeyframes.out.length) {
        viewElement.outTransition.__time = transitionKeyframes.out[0].time;
    }

    viewElement.time = element.time;
    viewElement.duration = element.duration;

    if ((viewElement.kind === NodeKind.Text || viewElement.kind === NodeKind.Button) &&
        (element.kind === NodeKind.Text || element.kind === NodeKind.Button)) {
        viewElement.font = element.font;
        viewElement.content = element.content;
    }
    else if (element.kind === NodeKind.Image && viewElement.kind === NodeKind.Image) {
        viewElement.imageAsset = element.imageAsset!;
        viewElement.feed = element.feed;
    }
    else if (element.kind === NodeKind.Widget && viewElement.kind === NodeKind.Widget) {
        viewElement.customProperties = element.customProperties;
    }

    return new Proxy(viewElement, {
        set: (obj, property, value) => {
            switch (property) {
                case 'ratio':
                case 'time':
                case 'duration':
                case 'font':
                case 'customProperties':
                case 'imageAsset':
                case 'content':
                case 'name':
                case 'inTransition':
                case 'outTransition':
                case 'parentId':
                    obj[property] = value;
                    obj.__data![property] = value;
                    break;
                default:
                    obj[property] = value;
                    break;
            }
            if (viewElement.__onChange) {
                viewElement.__onChange(obj, property, value);
            }
            return true;
        },
        get: (obj, property) => {
            switch (property) {
                case 'inTransition':
                case 'outTransition':
                case 'parentId':
                    return obj.__data![property];
                default:
                    return obj[property];
            }
        }
    });
}

export function createDataElement<T extends OneOfDataElements>(element: T): T {
    const proxy = new Proxy(element, {
        set: (obj, property, value): boolean => {
            if (property === '__noEmit') {
                obj.__noEmit = value;
                return true;
            }
            obj[property] = value;
            if (!obj.__noEmit) {
                if (property === 'content') {
                    for (const callback of obj.__rootNode!.callbacks) {
                        callback(element, {
                            property: property as any,
                            value,
                        });
                    }
                }
            }
            return true;
        },
        ownKeys(target: object): (string | number | symbol)[] {
            const ownKeys = Reflect.ownKeys(target);
            return ownKeys.filter(k => k !== '__rootNode');
        }
    });

    return proxy;
}

export interface IPreloadImage {
    frames: number[];
    format: 'jpg' | 'gif' | 'png';
    quality: number;
}

export interface IElementValues {
    versionProperties: IVersionProperty[];
    originalVersionProperties: IVersionProperty[];
    images?: ICollection<IImageAsset>;
    fontFamilies: IFontFamilies;
}

export interface IElementPropertyValues extends IElementValues {
    elementProperties: ICollection<IElementProperty>;
}

export function initializeElements(
    creative: CreativeDataNode,
    elements: ICollection<IElement>,
    values: IElementValues): void {
    forEachDataElement(creative, (element) => {
        const globalElement = elements.find(element.id);
        if (!globalElement) {
            throw new Error(`Could not find global element with id '${element.id}'.`);
        }
        element.__noEmit = true;
        element.name = globalElement.name;
        element.__noEmit = false;

        initializeElementProperties(element,
            {
                elementProperties: globalElement.properties,
                versionProperties: values.versionProperties,
                originalVersionProperties: values.originalVersionProperties,
                fontFamilies: values.fontFamilies,
                images: values.images,
            }
        );
    });
}

export function initializeFonts(textElement: ITextDataNode, fontFamilies: IFontFamilies): void {
    for (const style of textElement.characterStyles.values()) {
        if (style.font) {
            if (typeof style.font === 'string') {
                const fontStyle = fontFamilies.getFontStyleById(style.font);
                style.font = {
                    id: fontStyle.id,
                    src: fontStyle.fontUrl,
                    weight: fontStyle.weight,
                    style: fontStyle.italic ? 'italic' : 'normal',
                    fontFamilyId: fontStyle.fontFamilyId,
                };
            }
        }
    }
}

export function initializeElementProperties(
    element: OneOfDataElements,
    values: IElementPropertyValues): void {
    const { elementProperties, versionProperties: versionValues, originalVersionProperties: originalVersionValues } = values;

    if ((element.kind === NodeKind.Text || element.kind === NodeKind.Button) && element.__fontStyleId) {
        const fontStyle = element.font && element.font.id ?
            values.fontFamilies.getFontStyleById(element.font.id) :
            values.fontFamilies.getFontStyleById(element.__fontStyleId);

        if (!fontStyle) {
            throw new Error(`Could not find font style '${element.font.id}'.`);
        }
        element.font = {
            id: fontStyle.id,
            src: fontStyle.fontUrl,
            weight: fontStyle.weight,
            style: fontStyle.italic ? 'italic' : 'normal',
            fontFamilyId: fontStyle.fontFamilyId,
        };
    }

    for (const property of elementProperties) {
        if (property.versionPropertyId) {
            let versionValue = versionValues.find(v => v.id === property.versionPropertyId);
            if (!versionValue) {
                if (!originalVersionValues) {
                    throw new Error(`Could not find version property value 'property.versionPropertyId'.`);
                }
                versionValue = originalVersionValues.find(e => e.id === property.versionPropertyId);
            }
            if (!versionValue) {
                throw new Error('Could not find value from global elements, selected version or original.');
            }
            const name = property.name;
            if (name === 'content') {
                initializeFonts(element as ITextDataNode, values.fontFamilies);
                element[name] = createTextFromVersionedText(element, versionValue.value, (element as ITextDataNode).characterStyles);
            }
            else if (isWidgetNode(element)) {
                const customProperty = element.customProperties!
                    .find(prop => prop.versionPropertyId === property.versionPropertyId);
                if (customProperty) {
                    customProperty.value = serializeVersionedText(versionValue.value);
                }
            }
            else {
                element[name] = versionValue.value;
            }
        }
        else {
            element[property.name] = property.value;
        }
    }
}

export function createVersionedTextFromText(text: T.IText): IVersionedText {
    let position = 0;
    const textSpans: ITextSpan[] = [];
    let _text = '';
    for (const span of text.spans) {
        if (span.type === T.SpanType.End) {
            break;
        }
        const length = span.type === T.SpanType.Variable ? span.style.variable!.path.length + 1 : span.content.length;
        textSpans.push({
            position,
            length,
            type: span.type,
            styleIds: span.styleIds,
            variable: span.style.variable,
            __previousStyleIds: [...span.__previousStyleIds || []],
            __previousStyleIdToHistoryIndexMap: new Map(span.__previousStyleIdToHistoryIndexMap || [])
        });
        position += length;
        _text += span.type === T.SpanType.Variable ? T.VARIABLE_PREFIX + span.style.variable!.path : span.content;
    }
    return {
        text: _text,
        styles: textSpans,
    };
}

export function createInlineStyledTextFromText(text: T.IText): IInlineStyledText {
    let position = 0;
    const textSpans: IInlinedStyledTextSpan[] = [];
    let _text = '';
    for (const span of text.spans) {
        if (span.type === T.SpanType.End) {
            break;
        }
        const length = span.content.length;
        textSpans.push({
            position,
            length,
            type: span.type,
            style: span.style,
            variable: span.style.variable
        });
        position += length;
        _text += span.content;
    }
    return {
        text: _text,
        styles: textSpans,
    };
}

export function createTextFromInlineStyledText(inlineStyledText: IInlineStyledText): T.IText {
    const text = inlineStyledText.text;
    const spans: T.OneOfSpans[] = [];
    for (const span of inlineStyledText.styles) {
        switch (span.type) {
            case T.SpanType.Word:
            case T.SpanType.Variable:
            case T.SpanType.Space: {
                spans.push({
                    type: span.type,
                    attributes: {},
                    top: 0,
                    left: 0,
                    width: 0,
                    height: 0,
                    lineHeight: 0,
                    content: text.substr(span.position, span.length),
                    style: cloneDeep(span.style),
                    styleIds: new Map(),
                    __previousStyleIds: [],
                    __previousStyleIdToHistoryIndexMap: new Map<string, number>(),
                } as T.IWordSpan | T.ISpaceSpan);
                break;
            }
            case T.SpanType.Newline: {
                spans.push({
                    type: span.type,
                    attributes: {},
                    top: 0,
                    left: 0,
                    width: 0,
                    height: 0,
                    lineHeight: 0,
                    content: '\n',
                    style: cloneDeep(span.style),
                    styleIds: new Map(),
                    __previousStyleIds: [],
                    __previousStyleIdToHistoryIndexMap: new Map<string, number>(),
                } as T.INewlineSpan);
                break;
            }
            default:
                throw new TypeError(`Expected span of type 'word', 'space' 'newline', instead got '${span.type}'.`);
        }
    }

    spans.push({
        type: T.SpanType.End,
        content: 'END',
    } as T.IEndSpan);

    return {
        style: createDefaultTextProperties(),
        spans,
    };
}
export function createTextFromVersionedText(element: OneOfDataElements, versionedText: IVersionedText, elementsCharacterStyles: T.ICharacterStylesMap): T.IText {
    const text = versionedText.text;
    const spans: T.OneOfSpans[] = [];
    for (const span of versionedText.styles) {
        switch (span.type) {
            case T.SpanType.Variable:
                const styleId = span.styleIds.get(element.__rootNode!.id)!;
                spans.push({
                    type: span.type,
                    attributes: {},
                    top: 0,
                    left: 0,
                    width: 0,
                    height: 0,
                    lineHeight: 0,
                    content: T.VARIABLE_PREFIX + span.variable!.path,
                    style: Object.assign(cloneDeep(elementsCharacterStyles.get(styleId) || {}), {
                        variable: span.variable,
                    }),
                    styleIds: cloneMapDeep(span.styleIds),
                    styleId,
                    __previousStyleIds: [...span.__previousStyleIds || []],
                    __previousStyleIdToHistoryIndexMap: new Map(span.__previousStyleIdToHistoryIndexMap || [])
                });
                break;
            case T.SpanType.Word:
            case T.SpanType.Space: {
                const styleId = span.styleIds.get(element.__rootNode!.id)!;
                spans.push({
                    type: span.type,
                    attributes: {},
                    top: 0,
                    left: 0,
                    width: 0,
                    height: 0,
                    lineHeight: 0,
                    content: text.substr(span.position, span.length),
                    style: cloneDeep(elementsCharacterStyles.get(styleId) || {}),
                    styleIds: cloneMapDeep(span.styleIds),
                    styleId,
                    __previousStyleIds: [...span.__previousStyleIds || []],
                    __previousStyleIdToHistoryIndexMap: new Map(span.__previousStyleIdToHistoryIndexMap || [])
                } as T.IWordSpan | T.ISpaceSpan);
                break;
            }
            case T.SpanType.Newline: {
                const styleId = span.styleIds.get(element.__rootNode!.id)!;
                spans.push({
                    type: span.type,
                    attributes: {},
                    top: 0,
                    left: 0,
                    width: 0,
                    height: 0,
                    lineHeight: 0,
                    content: '\n',
                    style: cloneDeep(elementsCharacterStyles.get(span.styleIds.get(element.__rootNode!.id)!) || {}),
                    styleIds: cloneMapDeep(span.styleIds),
                    styleId,
                    __previousStyleIds: [...span.__previousStyleIds || []],
                    __previousStyleIdToHistoryIndexMap: new Map(span.__previousStyleIdToHistoryIndexMap || [])
                } as T.INewlineSpan);
                break;
            }
            default:
                throw new TypeError(`Expected span of type 'word', 'space' 'newline', instead got '${span.type}'.`);
        }
    }

    spans.push({
        type: T.SpanType.End,
        content: 'END',
    } as T.IEndSpan);

    return {
        style: createDefaultTextProperties(),
        spans,
    };
}

export function bindDataChangesToSharedElementsOrVersionedValues(dataTree: CreativeDataNode, elements: ICollection<IElement>, currentVersionId: string, versionedValues: IVersionProperty[], originalVersionValues?: IVersionProperty[]): (dataNode: CreativeDataNode, changes: any) => void {
    const callback = (dataNode, changes) => applyElementChanges(dataNode, changes, elements, currentVersionId, versionedValues, originalVersionValues);
    dataTree.subscribe(callback);
    return callback;
}

export function isBannerFlowLibraryWidget(element: IElement): boolean {
    // If the type is of bannerflowLibraryWidget then it's imported
    return element.type === NodeKind.BannerflowLibraryWidget || !!(element.properties && element.properties.find(prop => prop.name === 'bannerflowLibraryWidgetReference'));
}

export function isOriginalBannerFlowLibraryWidget(element: IElement): boolean {
    // If the type is of bannerflowLibraryWidget then it's imported
    return !!(element.type === 'widget' && element.properties && element.properties.find(prop => prop.name === 'bannerflowLibraryWidgetReference'));
}

// function hasSameVersionedText(source: IVersionedText, target: IVersionedText): boolean {
//     if (source.text !== target.text) {
//         return false;
//     }
//     if (source.styles.length !== target.styles.length) {
//         return false;
//     }
//     for (let i = 0; i < source.styles.length; i++) {
//         const sourceSpan = source.styles[i];
//         const targetSpan = target.styles[i];
//         if (sourceSpan.type !== targetSpan.type) {
//             return false;
//         }
//         if (sourceSpan.position !== targetSpan.position) {
//             return false;
//         }
//         if (sourceSpan.length !== targetSpan.length) {
//             return false;
//         }
//         if (!hasSameStyleIds(sourceSpan.styleIds, targetSpan.styleIds)) {
//             return false;
//         }
//         if (sourceSpan.variable === undefined && targetSpan.variable === undefined) {
//             continue;
//         }
//         else if (sourceSpan.variable && targetSpan.variable && sourceSpan.variable.spanId === targetSpan.variable.spanId) {
//             continue;
//         }
//         else {
//             return false;
//         }
//     }
//     return true;
// }

function applyElementChanges(dataNode: IElementNode, change: IChangeEvent, elements: ICollection<IElement>, currentVersionId: string, versionValues: IVersionProperty[], originalVersionValues?: IVersionProperty[]): void {
    const element = elements.find(e => e.id === dataNode.id)!;
    if (!element) {
        throw new Error(`Could not find element with id '${dataNode.id}'.`);
    }
    const property = element.properties.find(p => p.name === change.property);
    if (!property || !property.versionPropertyId) {
        return;
    }
    const value = typeof change.value !== 'undefined' ? change.value : undefined;
    const versionElementPropertyValue = versionValues.find(e => e.id === property.versionPropertyId);
    if (!versionElementPropertyValue) {
        versionValues.push({
            id: property.versionPropertyId,
            name: 'content',
            value: createVersionedTextFromText(value as T.IText),
        });
        return;
    }
    versionElementPropertyValue.value = createVersionedTextFromText(value as T.IText);
}
